import React, { useState, useEffect } from "react";
import {Button, ButtonGroup, Card, Combobox, comboboxFilterAndLimit, Input} from "@salesforce/design-system-react";
import { CONSTANT_ACCESS_REQUEST } from "../../../../constants/accessRequest";
import { ToastState } from "../../../../utils/types/toast";
import {createNewAccountUsingVending} from "../../../../services/accountVending";
import {CONSTANT_ACCOUNT} from "../../../../constants/accountManagement";
import {useAccountCreationStatus} from "../../../../context/account_vending/AccountCreationStatus";
import {HandleError} from "../../../utils/common";
import {TOAST_MESSAGE} from "../../../../constants/toastMessages";
import {fetchMyTeams} from "../../../../services/teams";
import {fetchMPAOrgUnits} from "../../../../services/orgUnit";


type AccountCreationFormProps = {
    isActive: boolean;
    toast: ToastState;
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
};

type MPAInfo = {
    value: string;
    label: string;
    id: string;
    isFalcon: boolean;
};

type ServiceTeam = {
    id: string;
    name: string;
    value: string;
    label: string;
    slack_channel: string;
    members: string[];
}

type MPAOU = {
    id: string;
    name: string;
    value: string;
    label: string;
    mpa: string;
    parentID: string;
    path: string;
}

const AccountCreation: React.FC<AccountCreationFormProps> = ({ isActive, toast, setToast }) => {
    const [accountName, setAccountName] = useState("");
    const [serviceTeamName, setServiceTeamName] = useState("");
    const [mpaID, setMpaID] = useState("");
    const [requesters, setRequesters] = useState("");
    const [gusProductTag, setGusProductTag] = useState("");
    const [ouID, setOUID] = useState("");
    const [allMPAOU, setAllMPAOU] = useState<MPAOU[]>([]);
    const [ouList, setOUList] = useState<any[]>([]);
    const [ouSearchInput, setOuSearchInput] = useState("");
    const [selectedOU, setSelectedOU] = useState<any>([]);
    const [mpaAccountIDs, setMpaAccountIDs] = useState<MPAInfo[]>([]);
    const [selectedMPA, setSelectedMPA] = useState<any>([]);
    const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(true);
    const [accountRequesterErrorMessage, setAccountRequesterErrorMessage] = useState("");
    const [gusProductTagErrorMessage, setGUSProductTagErrorMessage] = useState("");
    const [isBatchVendingRequest, setIsBatchVendingRequest] = useState<boolean>(false);
    const [totalRequestedAccounts, setTotalRequestedAccounts] = useState(1);
    const [isMPAListLoading, setIsMPAListLoading] = useState<boolean>(false);

    const {setAccountCreationStatus} = useAccountCreationStatus()

    const clearSelections = () => {
        setAccountName("");
        setServiceTeamName("");
        setMpaID("");
        setRequesters("");
        setGusProductTag("");
        setSelectedMPA([]);
        setTotalRequestedAccounts(1);
        setOUID("");
        setSelectedOU([]);
        setOUList([]);
        setOuSearchInput("");
    };

    useEffect(() => {
        if (isActive) {
            clearSelections();
            fetchServiceTeams();
            fetchAllMPAOUs();
        }
    }, [isActive]);

    useEffect(() => {
        setIsCreateButtonDisabled(!validateAccountInfo());
    }, [accountName, serviceTeamName, mpaID, requesters, gusProductTag, ouID]);

    const validateEmails = (input: string) => {
        if (input.length > 0) {
            const emailsArray = input.split(",").map((email) => email.trim());
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            for (let i = 0; i < emailsArray.length; i++) {
                const email = emailsArray[i];

                if (!emailRegex.test(email)) {
                    setAccountRequesterErrorMessage("Please enter a valid email address");
                    return false;
                }
            }
        }
        setAccountRequesterErrorMessage("");
        return true;
    };

    const validateGUSProductTag = (input: string) => {
        if (input.length === 0 || input.length === 18) {
            setGUSProductTagErrorMessage("");
            return true;
        }
        setGUSProductTagErrorMessage("Please enter a valid GUS Product Tag");
        return false;
    };

    const validateAccountInfo = () => {
        const hasAccountName = accountName !== "";
        const hasServiceTeamName = serviceTeamName !== "";
        const hasMPAID = mpaID !== "";
        const hasAccountRequestors = requesters !== "" && validateEmails(requesters);
        const hasGUSProductTag = gusProductTag !== "" && validateGUSProductTag(gusProductTag);
        const hasOUID = ouID !== "";

        return !(!hasAccountName || !hasServiceTeamName || !hasMPAID || !hasAccountRequestors || !hasGUSProductTag || !hasOUID);
    };

    const handleAccountNameUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAccountName(event.target.value);
    };

    const handleServiceTeamNameUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setServiceTeamName(event.target.value);
    };

    const handleAccountRequesterUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputValue = event.target.value;
        setRequesters(inputValue);
        validateEmails(inputValue);
    };

    const handleGusProductTagUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputValue = event.target.value;
        setGusProductTag(event.target.value);
        validateGUSProductTag(inputValue);
    };

    const handleCreateAccountRequest = async () => {
        setToast({
            isVisible: true,
            message: {
                heading: "Account Creation requested, please wait.",
            },
            variant: "warning",
        });
        const payload = {
            account_name: !isBatchVendingRequest ? accountName : "",
            account_name_prefix: isBatchVendingRequest ? accountName : "",
            team_name: serviceTeamName,
            organization_account_id: mpaID,
            account_type: "default-non-falcon-profile",
            account_owners: requesters,
            gus_product_tag: gusProductTag,
            target_org_unit_id: ouID,
            is_falcon: selectedMPA[0].isFalcon,
            total_accounts_requested: totalRequestedAccounts,
            is_batch_vending: isBatchVendingRequest,
        }
        createNewAccountUsingVending(payload).then(data => {
            clearSelections();
            setAccountCreationStatus({
                crt_id: data.client_request_token,
                status: CONSTANT_ACCOUNT.PROCESSING,
                account_id: "",
                error_status: ""
            });
            navigator.clipboard.writeText(data.client_request_token);
            setToast({
                isVisible: true,
                message: {
                    heading: "Account Creation in process, please wait.",
                    details: `Token id ${data.client_request_token} is copied to clipboard, please use it to check the status later`
                },
                variant: "success",
            });
        }).catch(error => {
            const toastSetter = {
                setToast: setToast
            };
            HandleError(toastSetter, error, TOAST_MESSAGE.ERROR_ACCOUNT_VENDING_CREATION);
        })

    };

    const handleAccountTypeChange = (type: boolean) => {
        setIsBatchVendingRequest(type);
    };

    const handleTotalAccountRequestUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        let value = parseInt(event.target.value, 10);

        // Enforce min/max constraints
        if (isNaN(value) || value < 1) {
            value = 1;
        } else if (value > 50) {
            value = 50;
        }
        setTotalRequestedAccounts(value);
    };

    const fetchServiceTeams = () => {
        setIsMPAListLoading(true)
        fetchMyTeams().then((data) => {
            const transformedData = data.map((serviceTeam: ServiceTeam) => ({
                id: serviceTeam.name,
                label: serviceTeam.name,
                value: serviceTeam.name,
                slack_channel: serviceTeam.slack_channel,
                members: serviceTeam.members
            }));
            setMpaAccountIDs(getMPAAccounts(transformedData));
        }).catch((error) => {
            const toastSetter = {
                setToast: setToast
            };
            HandleError(toastSetter, error, TOAST_MESSAGE.ERROR_UPDATING_SERVICE_TEAM);
        }).finally(() => {
            setIsMPAListLoading(false)
        })
    }

    const fetchAllMPAOUs = () => {
        fetchMPAOrgUnits().then((data) => {
            const transformedData = data.map((mpaOU: MPAOU) => ({
                id: mpaOU.id,
                name: mpaOU.name,
                label: mpaOU.path + " (" + mpaOU.id + ")",
                value: mpaOU.id,
                mpa: mpaOU.mpa,
                parentID: mpaOU.parentID,
                path: mpaOU.path
            }));
            setAllMPAOU(transformedData);
        }).catch((error) => {
            const toastSetter = {
                setToast: setToast
            };
            HandleError(toastSetter, error, TOAST_MESSAGE.ERROR_FETCHING_MPA_OU_LIST);
        })
    }

    const fetchOUList = (mpaId: string) => {

        const ssmMPAMetadata = `${process.env.REACT_APP_PCSK_ONBOARDED_MPA_METADATA}`;
        const MPAList = JSON.parse(ssmMPAMetadata);
        const selectedMPA = MPAList.find((account: { id: string; }) => {
            return account.id === mpaId;
        })

        let filteredAllOUs = allMPAOU.filter((mpaOU) => mpaOU.mpa === mpaId)

        if (selectedMPA?.allowed_ou_ids?.length && selectedMPA.allowed_ou_ids[0] === "*") {
            if (filteredAllOUs.length > 0) {
                setOUList(filteredAllOUs)
            } else {
                // Show default value as root
                const ouList = [{
                    value: "root",
                    label: "root",
                    id: "root",
                }]
                setOUList(ouList)
            }

        } else {
            let filteredList = allMPAOU.filter((ou) => selectedMPA.allowed_ou_ids.includes(ou.id))
            if (filteredList.length > 0) {
                setOUList(filteredList);
            } else {
                // Show default value as root
                const ouList = [{
                    value: "root",
                    label: "root",
                    id: "root",
                }]
                setOUList(ouList)
            }
        }
    }

    return (
        <Card heading={CONSTANT_ACCOUNT.NEW_ACCOUNT_REQUEST}>
            <div className="slds-p-around_small">
                <ButtonGroup id="button-group-more-icon">
                    <Button
                        label={CONSTANT_ACCOUNT.SINGLE_ACCOUNT}
                        onClick={() => handleAccountTypeChange(false)}
                        variant={!isBatchVendingRequest ? "brand" : "neutral"}
                    />
                    <Button
                        label={CONSTANT_ACCOUNT.MULTIPLE_ACCOUNTS}
                        onClick={() => handleAccountTypeChange(true)}
                        variant={isBatchVendingRequest ? "brand" : "neutral"}
                    />
                </ButtonGroup>
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_vending_account_name"
                        label={!isBatchVendingRequest ? CONSTANT_ACCOUNT.ACCOUNT_NAME : CONSTANT_ACCOUNT.ACCOUNT_NAME_PREFIX} // Dynamic label
                        required
                        value={accountName}
                        onChange={handleAccountNameUpdate}
                        placeholder={!isBatchVendingRequest ? "Enter Account Name" : "Enter Account Prefix"}
                    />
                    {isBatchVendingRequest && accountName && (
                        <p className="slds-text-color_weak slds-m-top_xx-small">
                            Tentative account names (with random suffix): {accountName}-xyz123, {accountName}-abc456, {accountName}-pqr789...
                        </p>
                    )}
                </div>
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_vending_service_team"
                        label={CONSTANT_ACCOUNT.SERVICE_TEAM}
                        required
                        value={serviceTeamName}
                        onChange={handleServiceTeamNameUpdate}
                        placeholder="Enter Service Team Name"
                    />
                </div>
                <div className="slds-p-around--xx-small">
                    <Combobox
                        id="account_vending_mpa"
                        events={{
                            onSelect: (event: any, data: any) => {
                                setSelectedMPA(data.selection);
                                setMpaID(data.selection[0].value);
                                setSelectedOU([]);
                                setOUID("");
                                setOuSearchInput("");
                                fetchOUList(data.selection[0].value)
                            },
                        }}
                        labels={{
                            label: CONSTANT_ACCOUNT.MPA_NAME,
                            placeholder: CONSTANT_ACCOUNT.MPA_NAME_PLACEHOLDER,
                        }}
                        required
                        menuPosition="absolute"
                        options={comboboxFilterAndLimit({
                            options: mpaAccountIDs,
                            selection: selectedMPA,
                            limit: 10000
                        })}
                        hasMenuSpinner={isMPAListLoading}
                        selection={selectedMPA}
                        variant="readonly"
                    />
                </div>
                {isBatchVendingRequest &&
                    <div className="slds-p-around--xx-small">
                        <Input
                            id="number_of_accounts_request"
                            type="number"
                            label={CONSTANT_ACCOUNT.NO_OF_ACCOUNT_REQUEST}
                            required
                            value={totalRequestedAccounts}
                            onChange={handleTotalAccountRequestUpdate}
                            placeholder="Enter total number of accounts"
                        />
                        <div className="slds-text-body--small slds-color__text_gray-9">
                            Maximum allowed: 50 accounts
                        </div>
                    </div>
                }
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_vending_requestors"
                        label={CONSTANT_ACCOUNT.ACCOUNT_REQUESTORS}
                        value={requesters}
                        onChange={handleAccountRequesterUpdate}
                        required
                        errorText={accountRequesterErrorMessage}
                        placeholder="List of account owners"
                    />
                </div>
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_vending_gus_p_tag"
                        label={CONSTANT_ACCOUNT.GUS_PRODUCT_TAG}
                        value={gusProductTag}
                        onChange={handleGusProductTagUpdate}
                        errorText={gusProductTagErrorMessage}
                        required
                        placeholder="Enter GUS Product Tag"
                    />
                </div>
                <div className="slds-p-around--xx-small">
                    <Combobox
                        id="account_vending_ou"
                        events={{
                            onSelect: (event: any, data: any) => {
                                setSelectedOU(data.selection);
                                setOUID(data.selection[0].value);
                            },

                            onChange: (event: any, data: any) => {
                                setOuSearchInput(data.value);
                            },
                            onRequestRemoveSelectedOption: (event: any, data: any) => {
                                setOuSearchInput("");
                                setSelectedOU([]);
                            },
                        }}
                        labels={{
                            label: CONSTANT_ACCOUNT.OU_ID,
                            placeholder: CONSTANT_ACCOUNT.OU_ARN_PLACEHOLDER,
                        }}
                        required
                        menuPosition="absolute"
                        options={comboboxFilterAndLimit({
                            inputValue: ouSearchInput,
                            options: ouList,
                            selection: selectedOU,
                            limit: 10000
                        })}
                        value={ouSearchInput}
                        selection={selectedOU}
                        menuItemVisibleLength={5}
                        variant="inline-listbox"
                    />
                </div>
                <div className="slds-p-left--xx-small slds-p-top--medium">
                    <Button
                        key="createAccessRequestButton"
                        label={CONSTANT_ACCESS_REQUEST.CREATE_BUTTON}
                        variant="brand"
                        onClick={handleCreateAccountRequest}
                        disabled={isCreateButtonDisabled}
                    />
                </div>
            </div>
        </Card>
    );
};

export default AccountCreation;

function getMPAAccounts(serviceTeams: ServiceTeam[]) {
    const ssmMPAMetadata = `${process.env.REACT_APP_PCSK_ONBOARDED_MPA_METADATA}`;
    const serviceTeamIds = serviceTeams.map(team => team.id);
    const MPAList = JSON.parse(ssmMPAMetadata);
    const filteredAccounts = MPAList.filter((account: { isFalcon: any; allowed_service_teams: any[] }) =>
        account.allowed_service_teams.some((teamId: any) => serviceTeamIds.includes(teamId))
    );

    return filteredAccounts.map((account: { id: any; label: any; value: any, isFalcon: any; allowed_service_teams: any; allowed_ou_ids: any}) => ({
        value: account.id,
        label: `${account.id} - ${account.label} - ${account.value}`,
        id: account.id,
        isFalcon: account.isFalcon,
        allowedServiceTeam: account.allowed_service_teams,
        allowedOUIds: account.allowed_ou_ids,
    }));
}